<template>
  <div :class="[expanded ? 'h-60' : 'h-auto', 'text-white bg-gray-900 w-full border border-gray-700']" data-testid="data-config-panel">
    <div class="flex items-center justify-between p-2 border-b border-gray-700 shadow">
      <div class="flex items-center">
        <p class="mb-0 mr-2 text-sm font-medium">Slide-level data configuration</p>
        <a
          v-if="userState.flags.MANAGE_DRILL_LABELS_ENABLED"
          class="flex w-4 h-4 p-0 text-white bg-transparent hover_text-gray-600"
          title="Edit master data config"
          data-testid="file-manager-btn"
          :href="fileManagerUrl"
          target="_blank"
        >
          <ArrowTopRightOnSquareIcon class="w-6" />
        </a>
      </div>
      <FontAwesomeIcon class="cursor-pointer" :icon="expanded ? 'chevron-down' : 'chevron-up'" @click="togglePanelVisibility" />
    </div>
    <div :class="['collapsible-container transition-all duration-200 ease-in-out', expanded ? 'expanded' : 'collapsed']">
      <div :class="['relative', 'flex-col', expanded ? 'expanded-content' : 'collapsed-content']">
        <div class="flex">
          <VButton
            :variant="allHighlightLabelDataClicked ? 'copper' : 'dark-gray-borderless'"
            size="xs"
            @click="allHighlightLabelDataClicked = !allHighlightLabelDataClicked"
          >
            <AdjustmentsVerticalIcon class="w-4 h-4" />
          </VButton>
          <p class="mb-0 ml-2 leading-8">All Highlight Label Data</p>

          <div v-if="allHighlightLabelDataClicked" ref="labelSizeMenu" class="absolute flex h-10 bg-gray-900 rounded-lg shadow -left-72">
            <EditorLabelSizeMenu v-if="!isAutoScale" :value="size" @input="handleSizeChange" />
            <div v-else class="flex flex-grow min-w-0">
              <EditorFontSizeSelector :value="size" :options="fontOptions" @input="handleSizeChange" />
            </div>
            <button
              class="flex items-center justify-center px-4 text-xs text-white border-l border-transparent gap-2 rounded-r-md box-border"
              :class="isAutoScale ? 'bg-copper-800 hover_bg-copper-900' : 'bg-gray-900 border-gray-800 hover_bg-gray-800'"
              @click="toggleAutoScale"
            >
              <span class="overflow-visible text-sm rounded-none"><FontAwesomeIcon icon="up-right-and-down-left-from-center" /></span>
              Autoscale on zoom
            </button>
          </div>
        </div>
        <div v-if="drillLabel" class="flex flex-col mt-4 text-sm gap-1">
          <div>{{ drillLabel.hole_id }}</div>
          <div>X: {{ drillLabel.api_coords.x.toLocaleString() }}</div>
          <div>Y: {{ drillLabel.api_coords.y.toLocaleString() }}</div>
          <div>Z: {{ drillLabel.api_coords.z.toLocaleString() }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed, watch } from 'vue'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import { AdjustmentsVerticalIcon } from '@heroicons/vue/24/solid'

import type { DrillDataSet } from '~/entities/drillLabel'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import { DEFAULT_FONT_SIZE, clampSize } from '~/lib/constants/labels'

const props = defineProps({
  selectedDrillLayerGroup: {
    type: Object as PropType<DrillDataSet | null>,
    default: null,
  },
  selectedDrillLabelId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const expanded = ref(true)
const allHighlightLabelDataClicked = ref(false)
const size = ref<number>(DEFAULT_FONT_SIZE)
const labelSizeMenu = ref<HTMLElement | undefined>()
const fileManagerUrl = ref<string | undefined>()
const isAutoScale = ref(true)
const config = useRuntimeConfig()
const { state: userState } = useUser()
const fontOptions = [
  { id: 400, name: 'Headline', classes: 'text-3xl' },
  { id: 300, name: 'Subheading', classes: 'text-2xl' },
  { id: 200, name: 'Body', classes: 'text-xl' },
]

useClickOutside([labelSizeMenu], ref(false), () => (allHighlightLabelDataClicked.value = false))

const drillDataSet = computed(() => {
  return props.selectedDrillLayerGroup || props.threeDViewer.world.drillDataSetManager?.getParentDataSet(props.selectedDrillLabelId!)
})

const drillLabel = computed(() => {
  if (!props.selectedDrillLabelId) return
  return props.threeDViewer.world.drillDataSetManager?.drillLabelManager.getLabel(props.selectedDrillLabelId)
})

watch(
  drillDataSet,
  drillDataSet => {
    if (!drillDataSet) return

    if (props.threeDViewer.world.drillDataSetManager?.drillLabelManager) {
      size.value = props.threeDViewer.world.drillDataSetManager.getLabelsTextSize(drillDataSet)
      isAutoScale.value = props.threeDViewer.world.drillDataSetManager.drillLabelManager.isSceneAutoScale()
    } else {
      size.value = DEFAULT_FONT_SIZE
      isAutoScale.value = true
    }
    fileManagerUrl.value = `${config.public.ADMIN_ENDPOINT}/data-files?search=${drillDataSet.data_set.name}`
  },
  { immediate: true }
)

function handleSizeChange(newSize: number) {
  if (!props.threeDViewer.world.drillDataSetManager || !drillDataSet.value) return

  const textSize = clampSize(newSize)
  size.value = textSize

  props.threeDViewer.world.drillDataSetManager.setLabelsTextSize(drillDataSet.value.data_set_id, textSize)
}

function togglePanelVisibility() {
  expanded.value = !expanded.value
}

function toggleAutoScale() {
  if (!props.threeDViewer.world.drillDataSetManager || !drillDataSet.value) return

  isAutoScale.value = !isAutoScale.value
  props.threeDViewer.world.drillDataSetManager?.setLabelsAutoScale(drillDataSet.value.data_set_id, isAutoScale.value)
  size.value = props.threeDViewer.world.drillDataSetManager?.getLabelsTextSize(drillDataSet.value)
}
</script>

<style lang="stylus" scoped>
.collapsible-container
  @apply relative
  .collapsed-content
    @apply w-0 overflow-hidden
  .expanded-content
    @apply h-full flex p-3
  .expanded-content:empty + .toggle-btn, .collapsed-content:empty + .toggle-btn
    @apply hidden pointer-events-none
  &.collapsed, &.collapsed
    @apply h-0 w-full
</style>
